import React, { JSX, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Maybe } from '@Types'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { WorkModel } from '@Entities/work/work'
import { Works } from '@Entities/work/works'
import { RightsholderClaimedShareList } from '@Entities/rightsholder'
import { FetchType } from '@Components/WorkItemV2/fetch-type-enum'
import WorkItemData from '@Components/WorkItemV2/work-item-data'
import { CardWithHeader } from '@Components/Tables/CardResults/card-with-header'
import ResultsCardTable from '@Components/Tables/ResultsCardTable'

const EMPTY_WORK_ITEMS: WorkItemData[] = []

export interface WorkResultsCardTableProps {
    works: Maybe<Works>
    paginationInfo: Maybe<PaginationInfo>
    limit: number
    page?: number
    loading: boolean
    /** Defaults to extended cards; pair with `compactWorkComponentType` to offer the compact view. */
    compact?: boolean
    onChangePage: (body: PaginationBody) => void
    onChangeLimit?: (newLimit: number) => void
    claimedShares?: Maybe<RightsholderClaimedShareList>
    compactWorkComponentType?: CardWithHeader<WorkItemData>
    extendedWorkComponentType: React.ComponentType<WorkItemData>
    fetchType?: FetchType
    noResultsInfo?: React.ReactNode
    showCompactToggle?: boolean
    bulkActionControlSection?: JSX.Element
    outerDivClassName?: string
    paginationResultsMessage?: string
    /** Per-row click side effect, e.g. flagging a redirect-in-progress before navigating away. */
    handleOnClick?: (work: WorkModel) => void
    /** Routes card navigation (e.g. public work view vs. rightsholder catalog); see WorkItemData. */
    displayType?: WorkItemData['displayType']
}

/**
 * Replacement for `WorkResultsTable`, built on a headless TanStack Table.
 *
 * Maps works onto `WorkItemData` cards and hands them to the generic `ResultsCardTable`.
 */
const WorkResultsCardTable = (props: WorkResultsCardTableProps) => {
    const {
        works,
        paginationInfo,
        limit,
        page,
        loading,
        compact,
        onChangePage,
        onChangeLimit,
        claimedShares,
        compactWorkComponentType,
        extendedWorkComponentType,
        fetchType,
        noResultsInfo,
        showCompactToggle,
        bulkActionControlSection,
        outerDivClassName,
        paginationResultsMessage,
        handleOnClick,
        displayType,
    } = props

    const workItemDataList: WorkItemData[] = useMemo(
        () =>
            works?.works?.map((work: WorkModel) => {
                const yourShare = claimedShares?.workClaimedShares?.find((share) => share.id === work.id)
                const workId = work.id ?? work.overclaimHeaderId ?? work.localId ?? work.propertyId ?? uuidv4()
                return {
                    work: work,
                    fetchType: fetchType,
                    yourShares: yourShare && yourShare.rightsholderClaimedShare,
                    getId: () => workId,
                    handleOnClick,
                    displayType,
                }
            }) ?? EMPTY_WORK_ITEMS,
        [works, claimedShares, fetchType, handleOnClick, displayType]
    )

    return (
        <ResultsCardTable
            items={workItemDataList}
            count={works?.count ?? 0}
            paginationInfo={paginationInfo}
            limit={limit}
            page={page}
            loading={loading}
            compact={compact}
            onChangePage={onChangePage}
            onChangeLimit={onChangeLimit}
            extendedCardType={extendedWorkComponentType}
            compactCardType={compactWorkComponentType}
            noResultsInfo={noResultsInfo}
            showCompactToggle={showCompactToggle}
            bulkActionControlSection={bulkActionControlSection}
            outerDivClassName={outerDivClassName}
            paginationResultsMessage={paginationResultsMessage}
        />
    )
}

export default WorkResultsCardTable
